/**
 * Admin Dashboard
 * Product management interface for administrators
 */

import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';

const AdminDashboard = () => {
  const api = useApi();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [products, setProducts] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState({ categories: [], subCategories: [] });
  
  const [filters, setFilters] = useState({
    manufacturer_id: '',
    category: '',
    search: ''
  });
  
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  // Check admin status
  useEffect(() => {
    let cancelled = false;
    
    const checkAdmin = async () => {
      try {
        await api.checkAdminStatus();
        if (cancelled) return;
        setIsAdmin(true);
        
        // Load data inline
        try {
          setLoading(true);
          const [prods, mfrs, cats] = await Promise.all([
            api.adminGetProducts(filters),
            api.adminGetManufacturers(),
            api.adminGetCategories()
          ]);
          if (!cancelled) {
            setProducts(prods);
            setManufacturers(mfrs);
            setCategories(cats);
          }
        } catch (err) {
          if (!cancelled) {
            setError(err.message);
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setIsAdmin(false);
          setError('You do not have admin access');
          setLoading(false);
        }
      }
    };
    
    checkAdmin();
    
    return () => {
      cancelled = true;
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [prods, mfrs, cats] = await Promise.all([
        api.adminGetProducts(filters),
        api.adminGetManufacturers(),
        api.adminGetCategories()
      ]);
      setProducts(prods);
      setManufacturers(mfrs);
      setCategories(cats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    loadData();
  };

  const handleEdit = (product) => {
    setEditingProduct({ ...product });
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
  };

  const handleSave = async () => {
    if (!editingProduct) return;
    
    try {
      setSaving(true);
      await api.adminUpdateProduct(editingProduct.id, editingProduct);
      setEditingProduct(null);
      loadData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditingProduct(prev => ({ ...prev, [field]: value }));
  };

  if (!isAdmin && !loading) {
    return (
      <div className="bg-red-500/10 border border-red-500 rounded-lg p-8 text-center">
        <h2 className="text-xl font-bold text-red-400 mb-2">Access Denied</h2>
        <p className="text-slate-400">You do not have permission to access the admin dashboard.</p>
      </div>
    );
  }

  if (loading && !isAdmin) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
        <p className="text-slate-400 mt-4">Checking admin access...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
        <span className="text-sm text-green-400 bg-green-500/10 px-3 py-1 rounded-full">
          Admin Access
        </span>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-slate-400 text-sm mb-2">Manufacturer</label>
            <select
              value={filters.manufacturer_id}
              onChange={(e) => handleFilterChange('manufacturer_id', e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
            >
              <option value="">All Manufacturers</option>
              {manufacturers.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-slate-400 text-sm mb-2">Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
            >
              <option value="">All Categories</option>
              {categories.categories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-slate-400 text-sm mb-2">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Product name or code..."
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700">
          <h2 className="font-semibold text-white">
            Products ({products.length})
          </h2>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500 mx-auto"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 border-b border-slate-700 bg-slate-900/50">
                  <th className="text-left py-3 px-4">Product</th>
                  <th className="text-left py-3 px-4">Category</th>
                  <th className="text-right py-3 px-4">Width (mm)</th>
                  <th className="text-right py-3 px-4">Length (m)</th>
                  <th className="text-right py-3 px-4">Coverage (m²)</th>
                  <th className="text-right py-3 px-4">Lap (mm)</th>
                  <th className="text-right py-3 px-4">RRP</th>
                  <th className="text-center py-3 px-4">Active</th>
                  <th className="text-center py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(product => (
                  <tr key={product.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="py-3 px-4">
                      <div className="text-white font-medium">{product.name}</div>
                      <div className="text-slate-500 text-xs">{product.manufacturer_name} • {product.product_code}</div>
                    </td>
                    <td className="py-3 px-4 text-slate-300">
                      <div>{product.category}</div>
                      <div className="text-slate-500 text-xs">{product.sub_category}</div>
                    </td>
                    <td className="py-3 px-4 text-right text-slate-300">{product.width_mm || '—'}</td>
                    <td className="py-3 px-4 text-right text-slate-300">{product.length_m || '—'}</td>
                    <td className="py-3 px-4 text-right text-slate-300">{product.coverage_m2 || '—'}</td>
                    <td className="py-3 px-4 text-right text-slate-300">{product.lap_width_mm || product.min_overlap_mm || '—'}</td>
                    <td className="py-3 px-4 text-right text-slate-300">
                      {product.rrp_per_unit ? `£${product.rrp_per_unit}` : '—'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-block w-2 h-2 rounded-full ${product.is_active ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => handleEdit(product)}
                        className="text-amber-500 hover:text-amber-400 text-sm font-medium"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">Edit Product</h3>
              <button onClick={handleCancelEdit} className="text-slate-400 hover:text-white">
                ✕
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-slate-400 text-sm mb-1">Name</label>
                  <input
                    type="text"
                    value={editingProduct.name || ''}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Product Code</label>
                  <input
                    type="text"
                    value={editingProduct.product_code || ''}
                    onChange={(e) => handleFieldChange('product_code', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Material</label>
                  <input
                    type="text"
                    value={editingProduct.material || ''}
                    onChange={(e) => handleFieldChange('material', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Category</label>
                  <select
                    value={editingProduct.category || ''}
                    onChange={(e) => handleFieldChange('category', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                  >
                    {categories.categories.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Sub-Category</label>
                  <input
                    type="text"
                    value={editingProduct.sub_category || ''}
                    onChange={(e) => handleFieldChange('sub_category', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>

              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-white font-medium mb-3">Dimensions & Coverage</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">Thickness (mm)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editingProduct.thickness_mm || ''}
                      onChange={(e) => handleFieldChange('thickness_mm', parseFloat(e.target.value) || null)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">Width (mm)</label>
                    <input
                      type="number"
                      value={editingProduct.width_mm || ''}
                      onChange={(e) => handleFieldChange('width_mm', parseInt(e.target.value) || null)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">Length (m)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editingProduct.length_m || ''}
                      onChange={(e) => handleFieldChange('length_m', parseFloat(e.target.value) || null)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">Coverage (m²)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingProduct.coverage_m2 || ''}
                      onChange={(e) => handleFieldChange('coverage_m2', parseFloat(e.target.value) || null)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">Lap Width (mm)</label>
                    <input
                      type="number"
                      value={editingProduct.lap_width_mm || ''}
                      onChange={(e) => handleFieldChange('lap_width_mm', parseInt(e.target.value) || null)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">Weight (kg/m²)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingProduct.weight_kg_per_m2 || ''}
                      onChange={(e) => handleFieldChange('weight_kg_per_m2', parseFloat(e.target.value) || null)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-white font-medium mb-3">Pricing & Status</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">RRP (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingProduct.rrp_per_unit || ''}
                      onChange={(e) => handleFieldChange('rrp_per_unit', parseFloat(e.target.value) || null)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-1">Fire Rating</label>
                    <input
                      type="text"
                      value={editingProduct.fire_rating || ''}
                      onChange={(e) => handleFieldChange('fire_rating', e.target.value)}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  
                  <div className="flex items-end">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingProduct.is_active}
                        onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                        className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-amber-500"
                      />
                      <span className="text-white">Active</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
